/**
 * frontend/src/modules/self-service-lab/lib/api.ts
 * Appels API Self Service Lab — VF.1.SSL.J3 + J5
 */

import { urlApi } from '@/lib/urlApi';
import { apiClient } from '@/lib/apiClient';
import type {
  LabKpi,
  LabQueryRequest,
  LabQueryResponse,
  SavedQueryListItem,
  SavedQueryDetailResponse,
  CreateSavedQueryBody,
  UpdateSavedQueryBody,
  ShareSavedQueryBody,
} from '../types/lab.types';

export async function fetchLabKpisCatalogue(): Promise<LabKpi[]> {
  const res = await apiClient.get<{ data: LabKpi[] }>('/lab/kpis-catalogue');
  return res.data.data ?? [];
}

export async function fetchLabDimensions(
  type: string,
  params?: {
    search?: string;
    brand_id?: string;
    page?: number;
    per_page?: number;
    // cascade F5 — J3-MODIFS-FRONTEND
    region_ids?: string[];
    wilaya_ids?: string[];
    merchandiser_ids?: string[];
    // SSL P5 lot 5-A — filtre temps peuplé par scope
    preset?: string;
    grain?: string;
  },
): Promise<unknown[]> {
  const res = await apiClient.get<{ data: unknown[] }>(`/lab/dimensions/${type}`, {
    params,
    // Axios serialise les tableaux en region_ids[]=val1&region_ids[]=val2 par défaut
    paramsSerializer: (p) => {
      const parts: string[] = [];
      for (const [key, val] of Object.entries(p)) {
        if (val === undefined || val === null) continue;
        if (Array.isArray(val)) {
          // Fastify parses repeated keys (key=v1&key=v2), NOT bracket notation (key[]=v1)
          val.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`));
        } else {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(val))}`);
        }
      }
      return parts.join('&');
    },
  });
  return res.data.data ?? [];
}

export async function runLabQuery(req: LabQueryRequest): Promise<LabQueryResponse> {
  const res = await apiClient.post<LabQueryResponse>('/lab/query/run', req);
  return res.data;
}

export async function previewLabQuery(req: LabQueryRequest): Promise<LabQueryResponse> {
  const res = await apiClient.post<LabQueryResponse>('/lab/query/preview', req);
  return res.data;
}

// ─── Saved Queries CRUD (J5) ──────────────────────────────────────────────────

export async function createSavedQuery(body: CreateSavedQueryBody): Promise<SavedQueryListItem> {
  const res = await apiClient.post<{ data: SavedQueryListItem }>('/lab/queries', body);
  return res.data.data;
}

export async function listSavedQueries(
  filter?: 'all' | 'mine' | 'shared' | 'templates',
): Promise<{ data: SavedQueryListItem[]; meta: { total: number } }> {
  const res = await apiClient.get<{ data: SavedQueryListItem[]; meta: { total: number } }>(
    '/lab/queries',
    { params: filter ? { filter } : undefined },
  );
  return res.data;
}

export async function getSavedQueryDetail(id: string): Promise<SavedQueryDetailResponse> {
  const res = await apiClient.get<{ data: SavedQueryDetailResponse }>(`/lab/queries/${id}`);
  return res.data.data;
}

export async function updateSavedQuery(
  id: string,
  body: UpdateSavedQueryBody,
): Promise<SavedQueryListItem> {
  const res = await apiClient.patch<{ data: SavedQueryListItem }>(`/lab/queries/${id}`, body);
  return res.data.data;
}

export async function deleteSavedQuery(id: string): Promise<void> {
  await apiClient.delete(`/lab/queries/${id}`);
}

export async function duplicateSavedQuery(id: string): Promise<SavedQueryListItem> {
  const res = await apiClient.post<{ data: SavedQueryListItem }>(`/lab/queries/${id}/duplicate`);
  return res.data.data;
}

export async function shareSavedQuery(
  id: string,
  body: ShareSavedQueryBody,
): Promise<SavedQueryListItem> {
  const res = await apiClient.post<{ data: SavedQueryListItem }>(`/lab/queries/${id}/share`, body);
  return res.data.data;
}

export async function runSavedQuery(id: string): Promise<LabQueryResponse> {
  const res = await apiClient.post<LabQueryResponse>(`/lab/queries/${id}/run`);
  return res.data;
}

// ─── Export Excel/PPT (J6) ────────────────────────────────────────────────────

export type LabExportFormat = 'excel' | 'ppt';

export interface ExportSyncResponse {
  mode: 'sync';
  filename: string;
  contentType: string;
  rowCount: number;
  data: string; // base64
}

export interface ExportAsyncResponse {
  mode: 'async';
  jobId: string;
  status: 'queued';
}

export type ExportInitResponse = ExportSyncResponse | ExportAsyncResponse;

export interface ExportStatusResponse {
  jobId: string;
  status: 'queued' | 'active' | 'completed' | 'failed';
  filename?: string;
  contentType?: string;
  rowCount?: number;
  error?: string;
}

export async function initiateLabExport(
  format: LabExportFormat,
  body: {
    queryResult: LabQueryResponse;
    queryRequest?: unknown;
    chartImageBase64?: string;
  },
): Promise<ExportInitResponse> {
  const res = await apiClient.post<ExportInitResponse>(`/lab/exports/${format}`, body);
  return res.data;
}


export function getLabExportDownloadUrl(jobId: string): string {
  // Lot 3 §5 — l'API vit sur son propre sous-domaine : l'adresse la nomme (relative en dev).
  return urlApi(`/api/v1/lab/exports/${jobId}/download`);
}
